import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CCol, CRow, CButton, CAlert, CSpinner, CCard, CCardBody } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilArrowLeft } from '@coreui/icons'
import { api, currentUser } from '../api/client'
import { fmtMoneda } from '../utils/moneda'

// Vista del Producto [RGM] 2026-10-05 (pedido Rafael: "a las filas producto,
// categoria y subcategoria etc debe llevar al view"): mismo formato de la
// factura — azul institucional #078bcd + tarjetas .factura-panel — con la
// lista del endpoint y la fila localizada por código (patrón CobradorViewPage).
// Día Venta: etiquetas EXACTAS del combo GX (producto.cs:3128-3135 — 0/vacío
// es el item vacío del combo → '—').
const PRIMARY_BLUE = '#078bcd'
const SALE_DAY_LABELS: Record<number, string> = {
  1: 'Lunes', 2: 'Martes', 3: 'Miercoles', 4: 'Jueves',
  5: 'Viernes', 6: 'Sabado', 7: 'Domingo',
}

const ProductoViewPage: React.FC = () => {
  const { codigo } = useParams<{ codigo: string }>()
  const navigate = useNavigate()
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [producto, setProducto] = useState<any>(null)
  const [cargando, setCargando] = useState(true)

  useEffect(() => {
    setCargando(true)
    // El listado ya trae todos los campos + joins a SubCategoría/Categoría
    // (CatalogosController GET — sin GET /catalogos/productos/{nro}); se
    // localiza la fila por código.
    api.get<any[]>(`/catalogos/productos?empresa=${empresa}`)
      .then((rows) => setProducto(
        (rows ?? []).find((r) => String(r.productoNro) === String(codigo)) ?? null))
      .catch(() => setProducto(null))
      .finally(() => setCargando(false))
  }, [codigo, empresa])

  if (cargando) {
    return (
      <div className="text-center py-5">
        <CSpinner color="primary" />
      </div>
    )
  }

  if (!producto) {
    return (
      <>
        <CRow className="mb-4">
          <CCol className="d-flex align-items-center justify-content-between">
            <h4 className="mb-0">Producto</h4>
            <CButton color="secondary" variant="outline" onClick={() => navigate('/catalogos')}>
              <CIcon icon={cilArrowLeft} className="me-1" size="sm" />
              Volver
            </CButton>
          </CCol>
        </CRow>
        <CAlert color="warning">Producto no encontrado</CAlert>
      </>
    )
  }

  const label = (txt: string) => <div className="factura-etiqueta">{txt}</div>
  const dayLabel = (v: any) => SALE_DAY_LABELS[Number(v ?? 0)] ?? '—'
  const hasValue = (v: any) =>
    v !== null && v !== undefined && String(v).trim() !== '' && Number(v) !== 0
  // Producto Libre: se muestran solo los campos que la fila trae (no vacíos).
  const freeFields = [
    { txt: 'Producto Libre Valor1', valor: producto.productoLibreValor1, moneda: true },
    { txt: 'Producto Libre Valor2', valor: producto.productoLIbreValor2, moneda: true },
    { txt: 'Producto Libre Valor3', valor: producto.productoLibreValor3, moneda: true },
    { txt: 'Producto Libre Numero1', valor: producto.productoLibreNumero1 },
    { txt: 'Producto Libre Numero2', valor: producto.productoLibreNumero2 },
    { txt: 'Producto Libre Numero3', valor: producto.productoLibreNumero3 },
    { txt: 'Producto Libre Caracter1', valor: producto.productoLibreCaracter1 },
    { txt: 'Producto Libre Caracter2', valor: producto.productoLibreCaracter2 },
  ].filter((f) => hasValue(f.valor))

  return (
    <>
      <CRow className="mb-3">
        <CCol className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h4 className="mb-0">Producto {producto.productoNro}</h4>
          <CButton color="secondary" variant="outline" onClick={() => navigate('/catalogos')}>
            <CIcon icon={cilArrowLeft} className="me-1" size="sm" />
            Volver
          </CButton>
        </CCol>
      </CRow>

      <CCard
        className="shadow-sm"
        style={{ borderRadius: 8, maxWidth: 920, borderTop: `4px solid ${PRIMARY_BLUE}` }}
      >
        <CCardBody style={{ padding: '22px 26px' }}>
          {/* ── Encabezado ── */}
          <div className="d-flex justify-content-between align-items-start border-bottom pb-3 mb-3 flex-wrap gap-2">
            <div>
              <div className="fs-4 fw-bold" style={{ color: PRIMARY_BLUE }}>
                {producto.productoNombre || 'Producto'}
              </div>
              <div className="text-body-secondary small">
                Empresa: {producto.empresaCodigo || '—'}
              </div>
            </div>
            <div className="text-end">
              <div className="fs-4 fw-bold" style={{ color: PRIMARY_BLUE }}>PRODUCTO</div>
              <div>Nro <span className="fw-semibold">{producto.productoNro}</span></div>
              <div className="fw-semibold" style={{ color: PRIMARY_BLUE }}>
                {fmtMoneda(producto.productoValor)}
              </div>
            </div>
          </div>

          <div className="row g-3">
            {/* ── Datos ── */}
            <div className="col-md-6">
              <div className="factura-panel h-100">
                <h6 className="fw-bold text-primary mb-2" style={{ fontSize: 13 }}>DATOS</h6>
                <div className="row g-1">
                  <div className="col-6">
                    {label('Nro')}<div>{producto.productoNro}</div>
                  </div>
                  <div className="col-6">
                    {label('Fecha')}<div>{String(producto.productoFecha ?? '').slice(0, 10) || '—'}</div>
                  </div>
                  <div className="col-6">
                    {label('Hora')}<div>{producto.productoHora || '—'}</div>
                  </div>
                  <div className="col-6">
                    {label('Nombre')}<div>{producto.productoNombre || '—'}</div>
                  </div>
                  <div className="col-12">
                    {label('Descripcion')}<div>{producto.productoDescripcion || '—'}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Precio / Stock ── */}
            <div className="col-md-6">
              <div className="factura-panel h-100">
                <h6 className="fw-bold text-primary mb-2" style={{ fontSize: 13 }}>PRECIO / STOCK</h6>
                <div className="row g-1">
                  <div className="col-6">
                    {label('Valor')}<div className="fw-semibold">{fmtMoneda(producto.productoValor)}</div>
                  </div>
                  <div className="col-6">
                    {label('Stock')}<div>{Number(producto.productoStock ?? 0)}</div>
                  </div>
                  <div className="col-6">
                    {label('Dia Venta')}<div>{dayLabel(producto.productoDiaVenta)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Clasificacion (joins del listado) ── */}
            <div className="col-12">
              <div className="factura-panel">
                <h6 className="fw-bold text-primary mb-2" style={{ fontSize: 13 }}>CLASIFICACION</h6>
                <div className="row g-1">
                  <div className="col-md-3">
                    {label('Sub Categoria Nro')}<div>{producto.subCategoriaNro ?? '—'}</div>
                  </div>
                  <div className="col-md-3">
                    {label('Sub Categoria Nombre')}<div>{producto.subCategoriaNombre || '—'}</div>
                  </div>
                  <div className="col-md-3">
                    {label('Categoria Nro')}<div>{producto.categoriaNro ?? '—'}</div>
                  </div>
                  <div className="col-md-3">
                    {label('Categoria Nombre')}<div>{producto.categoriaNombre || '—'}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Libres (solo los que la fila trae) ── */}
            {freeFields.length > 0 && (
              <div className="col-12">
                <div className="factura-panel">
                  <h6 className="fw-bold text-primary mb-2" style={{ fontSize: 13 }}>LIBRES</h6>
                  <div className="row g-1">
                    {freeFields.map((f) => (
                      <div key={f.txt} className="col-md-3">
                        {label(f.txt)}
                        <div>{f.moneda ? fmtMoneda(f.valor) : f.valor}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </CCardBody>
      </CCard>
    </>
  )
}

export default ProductoViewPage
